import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { brandAssets } from "../../data/site";
import { ActionLink } from "../ActionLink";
type HeroSectionProps = {
  assessmentUrl: string;
};
const credentials = ["Bilingual movement coach", "Professional dancer", "Choreographer", "Fitness professional", "Denver based", "Online worldwide"];
export function HeroSection({
  assessmentUrl
}: HeroSectionProps) {
  const reduceMotion = useReducedMotion();
  const reveal = reduceMotion ? {} : {
    initial: {
      opacity: 0,
      y: 20
    },
    animate: {
      opacity: 1,
      y: 0
    }
  };
  return <>
      <section id="top" className="relative min-h-screen overflow-hidden bg-cream pt-20 text-ink" data-id="element-147" data-mp="7av6jgxbiktu">
        <div className="mx-auto grid min-h-[calc(100vh-5rem)] max-w-[1600px] lg:grid-cols-[1.35fr_0.65fr]" data-id="element-148" data-mp="bbf2ih65niee">
          <div className="flex flex-col justify-center px-5 py-20 sm:px-8 lg:px-12 xl:px-20" data-id="element-149" data-mp="21o4jjip2oogu">
            <motion.p {...reveal} transition={{
            duration: 0.5,
            ease: [0.22, 1, 0.36, 1]
          }} className="text-[10px] font-bold uppercase tracking-[0.24em] text-bronze" data-id="element-150" data-mp="25qhnq2o68ebi">Leo Tovar Method · Holistic performance coaching</motion.p>
            <motion.h1 {...reveal} transition={{
            duration: 0.65,
            delay: 0.06,
            ease: [0.22, 1, 0.36, 1]
          }} className="mt-8 max-w-6xl font-display text-[clamp(3.8rem,8.2vw,8.8rem)] font-semibold uppercase leading-[0.82] tracking-[-0.055em]" data-id="element-151" data-mp="25ee42cc5pnob">Move better.<br data-id="element-152" data-mp="2f7bf5xetkacw" />Feel stronger.<br data-id="element-153" data-mp="1rf4p1fzoszlx" /><span className="text-bronze" data-id="element-154" data-mp="1rf4mp9c7snir">Live with purpose.</span></motion.h1>
            <motion.div {...reveal} transition={{
            duration: 0.6,
            delay: 0.13,
            ease: [0.22, 1, 0.36, 1]
          }} className="mt-9 max-w-2xl border-l border-bronze pl-6" data-id="element-155" data-mp="1rh7mum72zx17">
              <p className="text-base leading-8 text-ink/72 sm:text-lg" data-id="element-156" data-mp="21f5bhasurvuc">Personalized coaching built around how you move, what you’re experiencing, and what you want to achieve.</p>
            </motion.div>
            <motion.div {...reveal} transition={{
            duration: 0.6,
            delay: 0.2,
            ease: [0.22, 1, 0.36, 1]
          }} className="mt-9 flex flex-col gap-3 sm:flex-row" data-id="element-157" data-mp="3cr64x30nw8gf">
              <ActionLink href={assessmentUrl} className="w-full sm:w-auto" data-id="element-158" data-mp="3excg3dj8jfw3">Start your assessment</ActionLink>
              <ActionLink href="#method" variant="outline" className="w-full sm:w-auto" data-id="element-159" data-mp="3jv4i50dlgie0">Discover the method</ActionLink>
            </motion.div>
            <p className="mt-10 text-[10px] font-bold uppercase leading-6 tracking-[0.16em] text-ink/45" data-id="element-160" data-mp="3ovsbhspapbbe">Online coaching worldwide <span className="px-2 text-bronze" data-id="element-161" data-mp="3trr09hejcvcc">•</span> 1:1 coaching in Denver <span className="px-2 text-bronze" data-id="element-162" data-mp="2w7wgqtjadth9">•</span> English / Español</p>
          </div>
          <aside className="relative hidden overflow-hidden border-l border-ink/10 p-12 lg:flex lg:flex-col lg:justify-end" aria-label="Leo Tovar professional background" data-id="element-163" data-mp="3167lipuq5dsm">
            <img src={brandAssets.hero} alt="Leo Tovar, movement and performance coach" className="absolute inset-0 h-full w-full object-cover object-center" data-id="element-164" data-mp="3avhfycd1j8jw" />
            <div className="absolute inset-x-0 bottom-0 h-[46%] bg-ink/80" aria-hidden="true" data-id="element-165" data-mp="2de0wkbrbw938" />
            <div className="relative border-t border-sand pt-7 text-cream" data-id="element-166" data-mp="2iclmnicv9cdy">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-sand" data-id="element-167" data-mp="zjafvfzr4kzv">Movement-led experience</p>
              <ul className="mt-5 space-y-3" data-id="element-168" data-mp="vg4823wzqlra">{credentials.map(item => <li key={item} className="font-display text-2xl font-semibold uppercase tracking-[-0.02em]" data-id="element-169" data-mp="qi2s3ruv32k1">{item}</li>)}</ul>
            </div>
          </aside>
        </div>
      </section>
      <section className="bg-ink px-5 py-28 text-cream sm:px-8 sm:py-40 lg:px-12 lg:py-48" aria-labelledby="belief-title" data-id="element-170" data-mp="glhi0s3zj19c">
        <div className="mx-auto max-w-[1400px] text-center" data-id="element-171" data-mp="btg2mxbisuf3">
          <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-sand" data-id="element-172" data-mp="7sea0zc3zgm3">The first step</p>
          <h2 id="belief-title" className="mx-auto mt-8 max-w-6xl font-display text-6xl font-semibold uppercase leading-[0.85] tracking-[-0.05em] sm:text-8xl lg:text-[9rem]" data-id="element-173" data-mp="2u32iv9adi71">Understand how you move.</h2>
          <p className="mx-auto mt-10 max-w-2xl text-base leading-8 text-cream/65 sm:text-lg" data-id="element-174" data-mp="3p09g2hez0nyn">Movement gives us more information. The assessment turns that information into a clear next step.</p>
        </div>
      </section>
    </>;
}